import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon,
    TicketIcon,
    EyeIcon,
    EllipsisVerticalIcon,
    BookOpenIcon,
} from '@heroicons/react/20/solid'
import { Button } from '../../components/button'
import { Input, InputGroup } from '../../components/input'
import { Select } from '../../components/select'
import { Badge } from '../../components/badge'
import { Divider } from '../../components/divider'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/table'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from '../../components/dropdown'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from '../../components/custom/ResponseDialog'
import {
    Pagination,
    PaginationList,
    PaginationNext,
    PaginationPage,
    PaginationPrevious,
} from '../../components/pagination'
import { getPageRange } from '../../utils/paginationHelper'
import loadingImage from '../../assets/images/boat-loading.gif'
import { formatDateOnly } from 'utils/common'
import { UserIcon } from '@heroicons/react/16/solid'
import { GET_UNIPAL_CONFIG } from 'graphql/unipal/queries'
import i18n from 'i18n'
import DatePicker from 'react-flatpickr'
import { UPDATE_UNIPAL_CONFIG_STATUS } from 'graphql/unipal/mutations'

const UnipalConfig = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchString, setSearchString] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [categoryFilter, setCategoryFilter] = useState('')
    const [activateDialog, setActivateDialog] = useState({ open: false, id: null, name: '' })
    const [deactivateDialog, setDeactivateDialog] = useState({ open: false, id: null, name: '' })
    const [successDialog, setSuccessDialog] = useState({ open: false, message: '' })
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' })
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [effectiveFrom, setEffectiveFrom] = useState('2025-01-30')
    const [effectiveTo, setEffectiveTo] = useState('2028-01-30')
    const DEFAULT_FROM = '2025-01-30'
    const DEFAULT_TO = '2028-01-30'

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const { data, loading, error, refetch } = useQuery(GET_UNIPAL_CONFIG, {
        variables: {
            filter: {
                route_type: categoryFilter || null,
                effective_from: effectiveFrom,
                effective_to: effectiveTo,
            },
            page: {
                offset: page * rowsPerPage,
                limit: rowsPerPage,
            },
        },
        fetchPolicy: 'network-only',
    })

    // Update discount Status
    const [updateUnipalStatus, { loading: activateLoading }] = useMutation(UPDATE_UNIPAL_CONFIG_STATUS, {
        onCompleted: data => {
            if (data?.updateUnipalConfigStatus?.success) {
                setSuccessDialog({
                    open: true,
                    message: data.updateUnipalConfigStatus.message || 'Unipal discounts plan activated successfully',
                })
                setShowSucDialog(true)
                refetch()
            } else {
                setErrorDialog({
                    open: true,
                    message: data?.updateUnipalConfigStatus?.message || 'Failed to activate unipal discounts plan',
                })
                setShowErrDialog(true)
            }
            setActivateDialog({ open: false, id: null, name: '' })
            setDeactivateDialog({ open: false, id: null, name: '' })
        },
        onError: error => {
            setErrorDialog({
                open: true,
                message: error.message || 'An error occurred while activating discounts plan',
            })
            setShowErrDialog(true)
            setActivateDialog({ open: false, id: null, name: '' })
            setDeactivateDialog({ open: false, id: null, name: '' })
        },
    })

    const unipalItems = data?.getUnipalConfigs?.unipal_configs || []
    const totalCount = data?.getUnipalConfigs?.total_count || 0
    const totalPages = Math.ceil(totalCount / rowsPerPage)

    const filteredUnipal = unipalItems.filter(uni => {
        const matchesSearch =
            searchString === '' ||
            uni.influencer_name?.en?.toLowerCase().includes(searchString.toLowerCase()) ||
            uni.influencer_name?.ar?.includes(searchString)

        const matchesStatus = !statusFilter || uni.status?.code === statusFilter

        const matchCategory = !categoryFilter || uni.route_type === categoryFilter

        return matchesSearch && matchesStatus && matchCategory
    })

    const onClickSearch = () => {
        setPage(0)
    }

    const onClickSearchClear = () => {
        setSearchString('')
        setStatusFilter('')
        setCategoryFilter('')
        setPage(0)
        setEffectiveFrom(DEFAULT_FROM)
        setEffectiveTo(DEFAULT_TO)
    }

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    const getStatusBadge = status => {
        if (!status) return null

        const statusMap = {
            ACT: { color: 'emerald' },
            INA: { color: 'rose' },
        }

        const badgeConfig = statusMap[status.code] || { color: 'zinc' }

        return <Badge color={badgeConfig.color}>{status.description?.[i18n.language] || status.description?.en}</Badge>
    }
    const getCategoryBadge = categoryCode => {
        const categoryMap = {
            DMC: { color: 'blue', label: 'Domestic' },
            INL: { color: 'purple', label: 'International' },
        }
        const category = categoryMap[categoryCode] || { color: 'zinc', label: categoryCode }
        return <Badge color={category.color}>{t(category.label)}</Badge>
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setSuccessDialog({ open: false, message: '' })
        setShowErrDialog(false)
        setErrorDialog({ open: false, message: '' })
    }

    const handleActivate = (id, name) => {
        setActivateDialog({
            open: true,
            id,
            name,
        })
    }

    const handleDeactivate = (id, name) => {
        setDeactivateDialog({
            open: true,
            id,
            name,
        })
    }

    const confirmActivate = () => {
        if (activateDialog.id) {
            updateUnipalStatus({
                variables: {
                    unipal_item_id: activateDialog.id,
                    status_code: 'ACT',
                },
            })
        }
    }

    const confirmDeactivate = () => {
        if (deactivateDialog.id) {
            updateUnipalStatus({
                variables: {
                    unipal_item_id: deactivateDialog.id,
                    status_code: 'INA',
                },
            })
        }
    }
    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <BookOpenIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Manage Unipal Subscription')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily view, add, and edit unipal plans for your customers.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/unipal-config/add')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Item')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 mb-4 flex flex-col sm:flex-row flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[250px]">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    value={searchString}
                                    onChange={e => setSearchString(e.target.value)}
                                    name="search"
                                    id="search"
                                    placeholder={t('Search Config')}
                                />
                            </InputGroup>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={onClickSearch}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                onClick={onClickSearchClear}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>

                        {/* Status Select */}
                        <div className="flex-1 min-w-[180px]">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Status')}</label>
                            <Select
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                                className="w-full"
                            >
                                <option value="">{t('All Statuses')}</option>
                                <option value="ACT">{t('Active')}</option>
                                <option value="INA">{t('Inactive')}</option>
                            </Select>
                        </div>

                        {/* Category Select */}
                        <div className="flex-1 min-w-[180px]">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('Category')}</label>
                            <Select
                                value={categoryFilter}
                                onChange={e => setCategoryFilter(e.target.value)}
                                className="w-full"
                            >
                                <option value="">{t('All Categories')}</option>
                                <option value="DMC">{t('Domestic')}</option>
                                <option value="INL">{t('International')}</option>
                            </Select>
                        </div>
                    </div>
                    <div className="mt-4 mb-7 flex flex-col sm:flex-row flex-wrap gap-4">
                        <div className="flex-1">
                            <label
                                htmlFor="effective-date"
                                className="block text-sm font-medium leading-6 text-gray-900"
                            >
                                {t('Effective Date Range')} <span className="text-red/100">*</span>
                            </label>
                            <div>
                                <DatePicker
                                    options={{
                                        mode: 'range',
                                        dateFormat: 'Y-m-d',
                                        allowInput: false,
                                    }}
                                    value={[
                                        effectiveFrom ? new Date(effectiveFrom) : null,
                                        effectiveTo ? new Date(effectiveTo) : null,
                                    ]}
                                    placeholder={t('From - To')}
                                    onChange={dates => {
                                        setEffectiveFrom(dates[0] ? dates[0].toISOString().split('T')[0] : '')
                                        setEffectiveTo(dates[1] ? dates[1].toISOString().split('T')[0] : '')
                                    }}
                                    className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600 sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />
            {loading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {error && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch items!')}</div>
                </div>
            )}

            {!loading && !error && filteredUnipal.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!loading && !error && filteredUnipal.length > 0 && (
                <>
                    <div className="table-custom rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)] w-full">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Item')}</TableHeader>
                                    <TableHeader>{t('Category')}</TableHeader>
                                    <TableHeader>{t('Routes 🛣️')}</TableHeader>
                                    <TableHeader>{t('Trips 🚤')}</TableHeader>
                                    <TableHeader>{t('Daypass 🎫')}</TableHeader>
                                    <TableHeader>{t('Effective From')}</TableHeader>
                                    <TableHeader>{t('Effective To')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUnipal.map(discount => (
                                    <TableRow
                                        key={discount.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/unipal-config/details/${discount.unipal_item_id}`)}
                                        title={`discount #${discount.effective_from}`}
                                    >
                                        <TableCell className="min-w-[200px]">
                                            {discount?.item_type === 'DAYPASS' && (
                                                <div className="flex items-center gap-2 bg-blue-50 text-blue-700 py-2 rounded-lg">
                                                    <span className="text-lg">🎫</span>
                                                    <span className="text-sm font-semibold">Daypass</span>
                                                </div>
                                            )}

                                            {discount?.item_type === 'TRIP' && (
                                                <div className="flex items-center gap-2 bg-green-50 text-green-700 py-2 rounded-lg">
                                                    <span className="text-lg">🚢</span>
                                                    <span className="text-sm font-semibold">Trip</span>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="min-w-[200px]">
                                            {getCategoryBadge(discount.route_type)}
                                        </TableCell>
                                        <TableCell className="min-w-[150px]">
                                            <div className="font-medium text-gray-900">
                                                <div className="inline-flex items-center gap-2 px-2 py-1 bg-blue-50 font-medium text-gray-900 rounded-md">
                                                    {discount?.routes?.length > 0 ? (
                                                        <span>{discount.routes.length}</span>
                                                    ) : (
                                                        <span>N/A</span>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="min-w-[150px]">
                                            <div className="font-medium text-gray-900">
                                                <div className="inline-flex items-center gap-2 px-2 py-1 bg-blue-50 font-medium text-gray-900 rounded-md">
                                                    {discount?.trips?.length > 0 ? (
                                                        <span>{discount.trips.length}</span>
                                                    ) : (
                                                        <span>N/A</span>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="min-w-[150px]">
                                            <div className="font-medium text-gray-900">
                                                <div className="inline-flex items-center gap-2 px-2 py-1 bg-blue-50 font-medium text-gray-900 rounded-md">
                                                    {discount?.daypasses?.length > 0 ? (
                                                        <span>{discount.daypasses.length}</span>
                                                    ) : (
                                                        <span>N/A</span>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="min-w-[200px]">
                                            {formatDateOnly(discount?.effective_from)}
                                        </TableCell>
                                        <TableCell className="min-w-[200px]">
                                            {formatDateOnly(discount?.effective_to)}
                                        </TableCell>
                                        <TableCell className="min-w-[150px]">
                                            {getStatusBadge(discount.status)}
                                        </TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(
                                                                `/unipal-config/details/${discount.unipal_item_id}`
                                                            )
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/unipal-config/edit/${discount.unipal_item_id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                    {discount.status_code !== 'ACT' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleActivate(
                                                                        discount.unipal_item_id,
                                                                        discount.item_type
                                                                    )
                                                                }}
                                                            >
                                                                <PlusIcon />
                                                                <DropdownLabel>{t('Activate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                    {discount.status_code === 'ACT' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleDeactivate(
                                                                        discount.unipal_item_id,
                                                                        discount.item_type
                                                                    )
                                                                }}
                                                            >
                                                                <TrashIcon />
                                                                <DropdownLabel>{t('Deactivate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    {totalPages > 1 && (
                        <Pagination className="py-6">
                            <PaginationPrevious
                                onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                                isDisabled={page === 0}
                            />
                            <PaginationList>
                                {getPageRange(page, totalPages).map(index => (
                                    <PaginationPage
                                        key={index}
                                        current={index === page}
                                        onClick={() => handlePageChange(index)}
                                    >
                                        {index + 1}
                                    </PaginationPage>
                                ))}
                            </PaginationList>
                            <PaginationNext
                                onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                                isDisabled={page === totalPages - 1}
                            />
                        </Pagination>
                    )}
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={errorDialog.message}
                    onClose={handleDialogClose}
                />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={successDialog.message}
                    onClose={handleDialogClose}
                />
            )}
            {activateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Activate Unipal Plan')}
                    message={`${t('Are you sure you want to activate')} ${activateDialog.name}?`}
                    onPositive={confirmActivate}
                    onNegative={() => setActivateDialog({ open: false, id: null, name: '' })}
                    positiveActionText={activateLoading ? t('Activating...') : t('Activate')}
                    negativeActionText={t('Cancel')}
                />
            )}
            {deactivateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Deactivate Unipal Plan')}
                    message={`${t('Are you sure you want to deactivate')} ${deactivateDialog.name}?`}
                    onPositive={confirmDeactivate}
                    onNegative={() => setDeactivateDialog({ open: false, id: null, name: '' })}
                    positiveActionText={activateLoading ? t('Deactivating...') : t('Deactivate')}
                    negativeActionText={t('Cancel')}
                />
            )}
        </>
    )
}

export default UnipalConfig
